import * as React from 'react';
import MyTypes from 'MyTypes';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap'; 
import * as models from '../../models';
import * as companyActions from '../../store/companies/actions';
import { USER_ACTIONS } from 'store/enums';
import _ from 'lodash'; 

interface Props extends PropsFromRedux {          
    closeModal: () => void;
    show: boolean;
    companyId: number;
}

interface State {
    fileTypeId: number;
    description: string;
    selectedFile: File | null;
    hasUploadStarted: boolean; 
}

class CompanyFileUploadModal extends React.Component<Props, State> {
    public state: State = {
        fileTypeId: 0,
        description: '',
        selectedFile: null,
        hasUploadStarted: false
    };

    componentDidMount() {
        if (this.props.fileTypesDD.totalCount === 0) {
            this.props.requestCompanyFileTypes({ pageNo: 1, pageSize: 100 });
        }
    }
    
    componentDidUpdate(prevProps: Props) {
        if (prevProps.isUploading && !this.props.isUploading && this.state.hasUploadStarted) {            
            if (this.props.lastUserAction === USER_ACTIONS.COMPANY_UPDATE_FAILURE) {
                this.setState({ hasUploadStarted: false });
            } 
            else {
                this.resetAndClose();
            }
        }
    }

    public resetAndClose = () => {
        // Prevent closing via backdrop/escape clicks if the network call is mid-flight
        if (this.props.isUploading) return;
        
        this.setState({ fileTypeId: 0, description: '', selectedFile: null, hasUploadStarted: false });
        this.props.closeModal();
    }

    public handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            this.setState({ selectedFile: e.target.files[0] });
        }
    }

    public submitForm = (e: React.FormEvent) => {
        e.preventDefault();
        const { fileTypeId, description, selectedFile } = this.state;
        const { companyId } = this.props;

        if (!selectedFile || fileTypeId === 0) return;

        const payload: models.AddCompanyFile = {
            companyId: companyId,
            companyFileTypeId: fileTypeId,
            description: description,
            file: selectedFile
        };

        this.setState({ hasUploadStarted: true });
        this.props.uploadCompanyFile(payload);
    }

    public render() {
        const { show, fileTypesDD, isFileTypeLoading, isUploading } = this.props;
        const { fileTypeId, description, selectedFile } = this.state;

        return (
            <Modal centered show={show} onHide={this.resetAndClose} backdrop={isUploading ? 'static' : true}>
                <Modal.Header closeButton={!isUploading}>
                    <Modal.Title>Upload Company Reference File</Modal.Title>
                </Modal.Header>                
                <Form className="claim-modal" onSubmit={this.submitForm}>                    
                    <Modal.Body>  
                        {/* File Type Selection */}
                        <Form.Group as={Row} controlId="fileType" className="input-field-container mb-3">
                            <Form.Label column sm="4">File Type</Form.Label>
                            <Col sm="8">
                                <Form.Select 
                                    required
                                    disabled={isUploading}
                                    value={fileTypeId}
                                    onChange={(e) => this.setState({ fileTypeId: parseInt(e.target.value) })}
                                >
                                    <option value="">Choose Type...</option>
                                    {fileTypesDD.resourceList.filter(x => x.isActive).map((t: models.CompanyFileType) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                {isFileTypeLoading && <small className="text-muted">Loading types...</small>}
                            </Col>
                        </Form.Group>

                        {/* Description */}
                        <Form.Group as={Row} controlId="description" className="input-field-container mb-3">
                            <Form.Label column sm="4">Description</Form.Label>
                            <Col sm="8">
                                <Form.Control 
                                    as="textarea"
                                    rows={2}
                                    disabled={isUploading}
                                    placeholder="Brief notes about this document..."
                                    value={description}
                                    onChange={(e) => this.setState({ description: e.target.value })}
                                />
                            </Col>
                        </Form.Group>

                        {/* File Input */}
                        <Form.Group as={Row} controlId="fileUpload" className="input-field-container">
                            <Form.Label column sm="4">Select File</Form.Label>
                            <Col sm="8">
                                <Form.Control 
                                    type="file"
                                    required
                                    disabled={isUploading}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.html"
                                    onChange={this.handleFileChange}
                                />
                                <Form.Text className="text-muted small">
                                    PDF, Word, Excel, or HTML preferred.
                                </Form.Text>
                            </Col>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.resetAndClose} disabled={isUploading}>
                            CANCEL
                        </Button>
                        <Button 
                            variant="primary" 
                            type="submit" 
                            disabled={!selectedFile || fileTypeId === 0 || isUploading}
                        >
                            {isUploading ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                    SAVING...
                                </>
                            ) : (
                                'UPLOAD'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        );
    }
}

const mapStateToProps = (state: MyTypes.RootState) => ({
    fileTypesDD: state.companies.companyFileTypes.companyFileTypesList,
    isFileTypeLoading: state.companies.companyFileTypes.isCompanyFileTypesLoading,
    isUploading: state.companies.company.isCompanyFileUploading,
    lastUserAction: state.user.actions.lastUserAction 
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            uploadCompanyFile: companyActions.uploadCompanyFile,
            requestCompanyFileTypes: companyActions.requestCompanyFileTypesList
        },
        dispatch
    );

const connector = connect(mapStateToProps, mapDispatchToProps); 
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CompanyFileUploadModal);